'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Mail, Smartphone, X } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import type { WikiUser } from '../lib/wiki-auth';
import { loginWithCode, sendLoginCode } from '../lib/gamekee-api';
import './site-account-controls.css';
import { ALIYUN_CAPTCHA_PREFIX, ALIYUN_CAPTCHA_SCENE_ID, loadAliyunCaptcha, type AliyunCaptchaInstance, type AliyunCaptchaResult } from '../lib/aliyun-captcha';

export type DemoUser = WikiUser;

const CODE_COOLDOWN_SECONDS = 60;

/** Template-only authentication. Replace with server-verified sessions before production. */
export default function CommentLogin({ open, onOpenChange, onLogin }: { open: boolean; onOpenChange: (open: boolean) => void; onLogin: (user: DemoUser) => void }) {
  const [method, setMethod] = useState<'phone' | 'email'>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [captchaReady, setCaptchaReady] = useState(false);
  const captcha = useRef<AliyunCaptchaInstance | null>(null);
  const accountRef = useRef('');
  const methodRef = useRef(method);
  const account = method === 'phone' ? phone : email;
  accountRef.current = account;
  methodRef.current = method;
  const validate = () => {
    if (method === 'phone' ? !/^1[3-9]\d{9}$/.test(account.trim()) : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(account.trim())) {
      setError(method === 'phone' ? '请输入正确的 11 位手机号。' : '请输入正确的邮箱地址。');
      return false;
    }
    return true;
  };
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);
  useEffect(() => {
    if (!open) {
      setCountdown(0);
      return;
    }
    let disposed = false;
    setCaptchaReady(false);
    loadAliyunCaptcha().then(() => {
      if (disposed || !window.initAliyunCaptcha) return;
      window.initAliyunCaptcha({
        SceneId: ALIYUN_CAPTCHA_SCENE_ID,
        prefix: ALIYUN_CAPTCHA_PREFIX,
        mode: 'popup',
        element: '#aliyun-captcha-element',
        button: '#aliyun-captcha-trigger',
        language: 'cn',
        zIndex: 1000001,
        slideStyle: { width: 360, height: 40 },
        captchaVerifyCallback: async (captchaVerifyParam: string): Promise<AliyunCaptchaResult> => {
          setSending(true); setError(''); setNotice('');
          try {
            const result = await sendLoginCode(methodRef.current, accountRef.current.trim(), captchaVerifyParam);
            const passed = Boolean(result.captcha_result);
            if (passed) {
              setNotice(result.biz_result || '验证码已发送，请注意查收。');
              setCountdown(CODE_COOLDOWN_SECONDS);
            } else {
              setError(result.biz_result || '安全验证未通过，请重新验证。');
            }
            return { captchaResult: passed, bizResult: result.biz_result };
          } catch (cause) {
            const message = cause instanceof Error ? cause.message : '验证码发送失败，请稍后再试。';
            setError(message);
            return { captchaResult: false, bizResult: message };
          } finally { setSending(false); }
        },
        getInstance: (instance: AliyunCaptchaInstance) => {
          if (disposed) return;
          captcha.current = instance;
          setCaptchaReady(true);
        },
        fail: () => setError('安全验证未通过，请重新验证。'),
        onError: (info: { msg?: string }) => setError(info?.msg || '阿里云验证码初始化失败。'),
      });
    }).catch(cause => { if (!disposed) setError(cause instanceof Error ? cause.message : '阿里云验证码加载失败。'); });
    return () => {
      disposed = true;
      captcha.current?.hide?.();
      captcha.current?.destroy?.();
      captcha.current = null;
      setCaptchaReady(false);
      document.querySelector('#aliyunCaptcha-mask')?.remove();
      document.querySelector('#aliyunCaptcha-window-popup')?.remove();
    };
  }, [open]);
  const sendCode = () => {
    if (!validate() || sending || countdown > 0) return;
    if (!captchaReady || !captcha.current?.show) { setError('安全验证正在加载，请稍后再试。'); return; }
    setError(''); setNotice('');
    captcha.current.show();
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!validate() || !code.trim() || submitting) { if (!code.trim()) setError('请输入收到的验证码。'); return; }
    setSubmitting(true); setError('');
    try {
      const user = await loginWithCode(method, account.trim(), code.trim());
      onLogin(user); setCode(''); setNotice(''); setCountdown(0); onOpenChange(false);
    } catch (cause) { setError(cause instanceof Error ? cause.message : '登录失败，请稍后再试。'); }
    finally { setSubmitting(false); }
  };
  const codeButtonLabel = countdown > 0
    ? `${countdown}s`
    : sending
      ? '发送中…'
      : '获取验证码';
  return <Dialog open={open} onOpenChange={value => { setError(''); setNotice(''); setCode(''); if (!value) setCountdown(0); onOpenChange(value); }}>
    <DialogContent className="hero-login-dialog" showCloseButton={false}>
      <DialogClose className="hero-login-close" aria-label="关闭登录"><X size={20}/></DialogClose>
      <span className="hero-login-eyebrow">GAME WIKI ACCOUNT</span>
      <DialogTitle className="hero-login-title">登录 GAME WIKI</DialogTitle>
      <DialogDescription>登录后可在所有 Wiki 页面同步使用消息与个人中心。</DialogDescription>
      <div className="hero-login-methods" role="group" aria-label="登录方式">
        <button type="button" aria-pressed={method === 'phone'} onClick={() => { setMethod('phone'); setError(''); setNotice(''); setCode(''); }}><Smartphone size={17}/>手机号登录</button>
        <button type="button" aria-pressed={method === 'email'} onClick={() => { setMethod('email'); setError(''); setNotice(''); setCode(''); }}><Mail size={17}/>邮箱登录</button>
      </div>
      <form className="hero-login-form" onSubmit={submit} noValidate>
        <label htmlFor="community-account">{method === 'phone' ? '手机号' : '邮箱地址'}</label>
        <div className="hero-login-account">{method === 'phone' && <span>+86</span>}<input id="community-account" type={method === 'phone' ? 'tel' : 'email'} autoComplete={method === 'phone' ? 'tel-national' : 'email'} value={account} onChange={event => { (method === 'phone' ? setPhone : setEmail)(event.target.value); setError(''); }} placeholder={method === 'phone' ? '请输入手机号' : '请输入邮箱地址'} aria-describedby={error ? 'community-login-error' : undefined}/></div>
        <label htmlFor="community-code">验证码</label>
        <div className="hero-login-code"><input id="community-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={event => { setCode(event.target.value.replace(/\D/g, '')); setError(''); }} placeholder="请输入验证码"/><button type="button" onClick={sendCode} disabled={sending || countdown > 0} aria-live="polite">{codeButtonLabel}</button></div>
        <div id="aliyun-captcha-element" aria-hidden="true" />
        <button id="aliyun-captcha-trigger" type="button" hidden tabIndex={-1}>安全验证</button>
        {notice && <p className="hero-login-success" role="status">{notice}</p>}
        {error && <p id="community-login-error" className="hero-login-error" role="alert">{error}</p>}
        <button className="hero-login-submit" type="submit" disabled={submitting}>{submitting ? '登录中…' : '登录'}</button>
      </form>
      <p className="hero-login-note">发送验证码前需要完成阿里云安全验证。<br/>登录状态会同步到本站所有页面。</p>
    </DialogContent>
  </Dialog>;
}
